import React from 'react';

import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import ReactDOMServer from 'react-dom/server';
import ReactHtmlParser, {convertNodeToElement} from 'react-html-parser';

import {StyledUserContentDiv} from 'app/procedures/styles';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {
  nodeProcessors,
  NodeType,
  ProcessorExtraOptions,
} from './nodeProcessors';

interface Props {
  id?: string;
  mediaURL: string;
  mediaURLRead: string;
  content: string | React.ReactElement<'div'>;
  imageSize?: 'large' | 'small' | 'original';
  innerRef?:
    | React.MutableRefObject<HTMLDivElement | null>
    | ((ref: HTMLDivElement) => void);
}

export const UserContentContainer = React.memo(
  ({
    id,
    mediaURL,
    mediaURLRead,
    content,
    imageSize = 'large',
    innerRef,
    ...other
  }: Props) => {
    function transform(
      node: NodeType,
      index: number
    ): React.ReactElement<any, string | React.JSXElementConstructor<any>> {
      const opts: ProcessorExtraOptions = {
        mediaURL,
        mediaURLRead,
        imageSize,
      };

      for (let i = 0; i < nodeProcessors.length; i++) {
        const processor = nodeProcessors[i];

        if (processor.tag != null && processor.tag !== node.name) {
          continue;
        }

        if (processor.condition(node)) {
          const [updatedNode, finalElement] = processor.processor(
            node,
            index,
            transform,
            opts
          );

          if (updatedNode != null) {
            node = updatedNode;
          }

          if (finalElement != null) {
            return finalElement;
          }
        }
      }

      return convertNodeToElement(node, index, transform);
    }

    return (
      <StyledUserContentDiv
        id={id}
        ref={innerRef}
        className={`fr-view ${
          isRightToLeftWrapper(content) ? 'right-to-left-text' : ''
        }`}
      >
        <div {...other}>
          {ReactHtmlParser(
            typeof content === 'string'
              ? content
              : ReactDOMServer.renderToStaticMarkup(content),
            {
              transform,
            }
          )}
        </div>
      </StyledUserContentDiv>
    );
  },
  (prevProps: Props, props: Props) => {
    const clonedProps = omit(prevProps, 'innerRef');
    const clonedNextProps = omit(props, 'innerRef');
    return isEqual(clonedProps, clonedNextProps);
  }
);
